<template>
  <div class="information-owner-table fbc-table-header-labels" data-testid="information-owner-table">
    <v-card-text v-if="loading" data-testid="information-owner-table__loading">
      <v-skeleton-loader type="table-heading, list-item-two-line, article" />
    </v-card-text>

    <template v-else>
      <div
        v-if="ownerRows.length === 0 && !hasActiveTableFilter && !searchQuery"
        class="information-owner-table__empty pa-8 text-center"
        data-testid="information-owner-table__empty"
      >
        <p class="grey--text mb-4">No information owners yet.</p>
        <v-btn
          v-if="canEdit"
          color="primary"
          class="white--text"
          data-testid="information-owner-table__empty-add-btn"
          @click="$emit('add-owner')"
        >
          <v-icon class="mr-1">mdi-plus</v-icon>
          <span>Add Owner</span>
        </v-btn>
      </div>

      <div v-else class="information-owner-table__datatable-wrap">
        <lb-datatableaction
          ref="dataTableAction"
          :headers="headers"
          :tabledata="ownerRows"
          :displayed-row-count="displayedRows.length"
          :itemsperpage="itemsPerPage"
          :bulk-actions="false"
          :selectfilter="selectFilter"
          :page_details="pageDetails"
          :searchterm="searchQuery"
          header-bg-class="fbc-table-header-bg"
          data-testid="information-owner-table__datatable"
          @filter-item="onFilterItem"
          @filter-active-change="onFilterActiveChange"
          @clear-item="onClearItem"
        >
          <template #tbody>
            <tr
              v-for="(item, index) in displayedRows"
              :key="item._id"
              class="information-owner-table__row"
              :class="{ 'information-owner-table__row--disabled': !item.status }"
              :data-testid="`information-owner-table__row--${index}`"
            >
              <template v-for="header in visibleHeaders">
                <td
                  v-if="header.visible && header.value === 'name'"
                  :key="`${header.value}-${item._id}`"
                  class="px-4 single-line"
                  data-testid="information-owner-table__owner-cell"
                >
                  <div class="information-owner-table__owner d-flex align-center">
                    <FcUserAvatar
                      :name="item.name"
                      :email="item.email"
                      :size="32"
                      data-test-id="information-owner-table__avatar"
                    />
                    <div class="information-owner-table__owner-text ml-3">
                      <span class="information-owner-table__owner-name font-weight-medium">
                        {{ item.name || '—' }}
                      </span>
                      <span class="information-owner-table__owner-email">
                        {{ item.email || '—' }}
                      </span>
                    </div>
                  </div>
                </td>

                <td
                  v-else-if="header.visible && header.value === 'totalRequest'"
                  :key="`${header.value}-${item._id}`"
                  class="px-4 single-line"
                  data-testid="information-owner-table__total-cell"
                >
                  {{ item.totalRequestLabel || '—' }}
                </td>

                <td
                  v-else-if="header.visible && header.value === 'pendingRequest'"
                  :key="`${header.value}-${item._id}`"
                  class="px-4 single-line"
                  data-testid="information-owner-table__pending-cell"
                >
                  {{ item.pendingRequestLabel || '—' }}
                </td>

                <td
                  v-else-if="header.visible && header.value === 'lastFollowupDate'"
                  :key="`${header.value}-${item._id}`"
                  class="px-4 single-line"
                  data-testid="information-owner-table__followup-date-cell"
                >
                  {{ formatFollowupDate(item.lastFollowupDate) }}
                </td>

                <td
                  v-else-if="header.visible && header.value === 'action'"
                  :key="`${header.value}-${item._id}`"
                  class="px-4 single-line border-right action-data"
                  data-testid="information-owner-table__action-cell"
                >
                  <OwnerActionCell
                    :enabled="item.status"
                    :show-follow-up="Number(item.totalRequest) > 0"
                    :show-reassign="Number(item.totalRequest) > 0"
                    :can-edit="canEdit"
                    :can-delete="canDelete"
                    :follow-up-loading="actionLoadingId === item._id && actionType === 'follow-up'"
                    :toggle-loading="actionLoadingId === item._id && actionType === 'toggle'"
                    :delete-loading="actionLoadingId === item._id && actionType === 'delete'"
                    @follow-up="$emit('follow-up', item)"
                    @toggle-status="$emit('toggle-status', { owner: item, enabled: $event })"
                    @edit="$emit('edit', item)"
                    @reassign="$emit('reassign', item)"
                    @delete="$emit('delete', item)"
                  />
                </td>
              </template>
            </tr>
          </template>
        </lb-datatableaction>
      </div>
    </template>
  </div>
</template>

<script>
import './InformationOwnerTable.scss';
import { FcUserAvatar } from '@/modules/financialclose/components';
import OwnerActionCell from './OwnerActionCell.vue';
import { OWNER_TABLE_HEADERS } from '../../utils/ownerSummary';

const PAGE_DETAILS = { page: 1, total: 0 };

export default {
  name: 'InformationOwnerTable',
  components: {
    FcUserAvatar,
    OwnerActionCell,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    ownerRows: {
      type: Array,
      default: () => [],
    },
    displayedRows: {
      type: Array,
      default: () => [],
    },
    searchQuery: {
      type: String,
      default: '',
    },
    selectFilter: {
      type: Object,
      default: () => ({}),
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    actionLoadingId: {
      type: String,
      default: null,
    },
    actionType: {
      type: String,
      default: null,
    },
    itemsPerPage: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      hasActiveTableFilter: false,
      headers: OWNER_TABLE_HEADERS.map((header) => ({ ...header })),
      pageDetails: { ...PAGE_DETAILS },
    };
  },
  computed: {
    visibleHeaders() {
      return this.headers.filter((header) => header.visible);
    },
  },
  methods: {
    formatFollowupDate(value) {
      if (!value) return '—';
      return this.$nova.formatDate(value);
    },
    onFilterItem(item) {
      this.$emit('filter-item', item);
    },
    onFilterActiveChange(active) {
      this.hasActiveTableFilter = active;
      this.$emit('filter-active-change', active);
    },
    onClearItem() {
      this.$emit('clear-item');
    },
    clearTableFilter() {
      this.$refs.dataTableAction?.clearFilter();
    },
  },
};
</script>
